import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateStudentProfile } from "../../services/admin.services";
import { changeUserPassword } from "../../services/user.services";
import { setUserDetails } from "../../state/reducers/userSlice";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

function EditProfileStudent() {
  const dispatch = useDispatch();
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const userDetails = useSelector((state) => state.user.userDetails);
  const role = useSelector((state) => state.user.role);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    student_id: "",
    first_name: "",
    last_name: "",
    username: "",
    phone_number: "",
    email: "",
    profile_picture_url: "",
    date_of_birth: "",
    city: "",
    gender: "",
    profileImage: "",
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [profileSuccess, setProfileSuccess] = useState("");
  const [profileError, setProfileError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({});
  const [passwordValidationErrors, setPasswordValidationErrors] = useState({});
  const [isProfileSubmitting, setIsProfileSubmitting] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

  // Sync Redux store data to local state
  useEffect(() => {
    if (userDetails) {
      setFormData({
        student_id: userDetails?.student_id || "",
        first_name: userDetails?.first_name || "",
        last_name: userDetails?.last_name || "",
        username: userDetails?.username || "",
        phone_number: userDetails?.phone_number || "",
        email: userDetails?.email || "",
        profile_picture_url: userDetails?.profile_picture_url || "",
        date_of_birth: userDetails?.date_of_birth || "",
        city: userDetails?.city || "",
        gender: userDetails?.gender || "",
        profileImage: "",
      });
    }
  }, [userDetails]);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // PhoneInput provides validation, just check if it's not empty when provided
    return !phone || phone.length >= 10; // Minimum 10 digits for international format
  };

  const validateName = (name) => {
    return name.trim().length >= 2 && /^[a-zA-Z\s]+$/.test(name);
  };

  const validatePassword = (password) => {
    // At least 8 characters, one uppercase, one lowercase, one number
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const validateAge = (dateOfBirth) => {
    if (!dateOfBirth) return true; // Optional field
    const today = new Date();
    const birthDate = new Date(dateOfBirth);

    // Just ensure the date is not in the future
    return birthDate <= today;
  };

  const validateProfileForm = () => {
    const errors = {};

    if (!formData.first_name.trim()) {
      errors.first_name = "First name is required";
    } else if (!validateName(formData.first_name)) {
      errors.first_name =
        "First name must be at least 2 characters and contain only letters";
    }

    if (!formData.last_name.trim()) {
      errors.last_name = "Last name is required";
    } else if (!validateName(formData.last_name)) {
      errors.last_name =
        "Last name must be at least 2 characters and contain only letters";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (formData.phone_number && !validatePhone(formData.phone_number)) {
      errors.phone_number =
        "Please enter a valid phone number (minimum 10 digits)";
    }

    if (formData.date_of_birth && !validateAge(formData.date_of_birth)) {
      errors.date_of_birth = "Date of birth cannot be in the future";
    }

    if (formData.city && formData.city.trim().length < 2) {
      errors.city = "City name must be at least 2 characters";
    }

    if (formData.profileImage) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(formData.profileImage.type)) {
        errors.profileImage =
          "Please upload a valid image file (JPEG, PNG, GIF)";
      } else if (formData.profileImage.size > maxSize) {
        errors.profileImage = "Image size must be less than 5MB";
      }
    }

    return errors;
  };

  const validatePasswordForm = () => {
    const errors = {};

    if (!passwordData.oldPassword) {
      errors.oldPassword = "Current password is required";
    }

    if (!passwordData.newPassword) {
      errors.newPassword = "New password is required";
    } else if (!validatePassword(passwordData.newPassword)) {
      errors.newPassword =
        "Password must be at least 8 characters with uppercase, lowercase, and number";
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = "Please confirm your new password";
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (
      passwordData.oldPassword &&
      passwordData.newPassword &&
      passwordData.oldPassword === passwordData.newPassword
    ) {
      errors.newPassword =
        "New password must be different from current password";
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear validation errors for the field being edited
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: "" });
    }

    // Reset messages when user starts making changes
    setProfileError("");
    setProfileSuccess("");
  };

  const handlePhoneChange = (value, countryData, event, formattedValue) => {
    setFormData({ ...formData, phone_number: value });

    // Clear validation errors for phone number
    if (validationErrors.phone_number) {
      setValidationErrors({ ...validationErrors, phone_number: "" });
    }

    // Reset messages when user starts making changes
    setProfileError("");
    setProfileSuccess("");
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });

    // Clear validation errors for the field being edited
    if (passwordValidationErrors[name]) {
      setPasswordValidationErrors({ ...passwordValidationErrors, [name]: "" });
    }

    // Reset messages when user starts making changes
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, profileImage: file });

    // Clear validation error for profile image
    if (validationErrors.profileImage) {
      setValidationErrors({ ...validationErrors, profileImage: "" });
    }
  };

  const handlePasswordSave = async () => {
    // Reset status messages
    setPasswordError("");
    setPasswordSuccess("");
    setPasswordValidationErrors({});

    // Validate form
    const errors = validatePasswordForm();
    if (Object.keys(errors).length > 0) {
      setPasswordValidationErrors(errors);
      return;
    }

    setIsPasswordSubmitting(true);

    try {
      // Call the API service with the required password data
      const passwordUpdateData = {
        old_password: passwordData.oldPassword,
        new_password: passwordData.newPassword,
      };

      const response = await changeUserPassword(passwordUpdateData);

      // Clear password fields after successful update
      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setPasswordSuccess("Password changed successfully!");
    } catch (error) {
      console.error("Error changing password:", error);
      // Extract error message from API response if available
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to change password. Please try again.";
      setPasswordError(errorMessage);
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    // Reset status messages
    setProfileError("");
    setProfileSuccess("");
    setValidationErrors({});

    // Validate form
    const errors = validateProfileForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsProfileSubmitting(true);

    const updatedFormData = new FormData();
    updatedFormData.append("student_id", formData.student_id);
    updatedFormData.append("first_name", formData.first_name.trim());
    updatedFormData.append("last_name", formData.last_name.trim());
    updatedFormData.append("username", formData.username);
    updatedFormData.append("phone_number", formData.phone_number.trim());
    updatedFormData.append("email", formData.email.trim());
    updatedFormData.append(
      "profile_picture_url",
      formData.profile_picture_url || ""
    );
    updatedFormData.append("date_of_birth", formData.date_of_birth);
    updatedFormData.append("city", formData.city.trim());
    updatedFormData.append("gender", formData.gender);

    if (formData.profileImage) {
      updatedFormData.append("profile_picture", formData.profileImage);
    }

    try {
      const response = await updateStudentProfile(
        formData.student_id,
        updatedFormData
      );

      // Update the formData with the response data if needed
      const updatedUserData = {
        ...formData,
        profile_picture_url:
          response.profile_picture_url || formData.profile_picture_url,
      };

      // Update Redux store after successful update
      dispatch(setUserDetails(updatedUserData));

      setProfileSuccess("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update profile. Please try again.";
      setProfileError(errorMessage);
    } finally {
      setIsProfileSubmitting(false);
    }
  };

  return (
    <div
      className={`flex-1 bg-white ${
        isSideBarOpen ? "ml-50" : "ml-20"
      } min-h-screen p-6 mx-auto`}
    >
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)} // Go back to the previous page
        className="mb-4 bg-gray-300 text-gray-700 px-4 py-2 rounded"
      >
        Back
      </button>
      <h1 className="text-2xl font-bold mb-6">Edit Profile</h1>
      <div className="flex flex-col lg:flex-row justify-between w-full gap-6">
        {/* Profile Form */}
        <div className="w-full lg:w-1/2 mx-auto mt-8 bg-white p-6 rounded shadow">
          <h1 className="text-2xl font-bold mb-6">
            {formData.first_name} {formData.last_name}
          </h1>

          {/* Success message */}
          {profileSuccess && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {profileSuccess}
            </div>
          )}

          {/* Error message */}
          {profileError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {profileError}
            </div>
          )}

          <form>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profile Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200"
                />
                {validationErrors.profileImage && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.profileImage}
                  </p>
                )}
                {formData.profile_picture_url && (
                  <p className="text-sm text-gray-500 mt-2">
                    Current Profile Image:{" "}
                    <a
                      href={formData.profile_picture_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 underline"
                    >
                      View Image
                    </a>
                  </p>
                )}
                {formData.profileImage && (
                  <p className="text-sm text-gray-500 mt-2">
                    New Image Selected: {formData.profileImage.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 ${
                    validationErrors.first_name
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {validationErrors.first_name && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.first_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 ${
                    validationErrors.last_name
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {validationErrors.last_name && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.last_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 bg-gray-100 ${
                    validationErrors.email
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  readOnly
                />
                {validationErrors.email && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <PhoneInput
                  country={"us"}
                  value={formData.phone_number}
                  onChange={handlePhoneChange}
                  inputStyle={{
                    width: "100%",
                    height: "42px",
                    fontSize: "14px",
                    border: validationErrors.phone_number
                      ? "1px solid #ef4444"
                      : "1px solid #d1d5db",
                    borderRadius: "6px",
                    paddingLeft: "48px",
                  }}
                  containerStyle={{
                    width: "100%",
                  }}
                  buttonStyle={{
                    border: validationErrors.phone_number
                      ? "1px solid #ef4444"
                      : "1px solid #d1d5db",
                    borderRadius: "6px 0 0 6px",
                  }}
                />
                {validationErrors.phone_number && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.phone_number}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 ${
                    validationErrors.date_of_birth
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {validationErrors.date_of_birth && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.date_of_birth}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Enter city"
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 ${
                    validationErrors.city ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {validationErrors.city && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.city}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleUpdate}
                disabled={isProfileSubmitting}
                className={`px-4 py-2 rounded text-white ${
                  isProfileSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                {isProfileSubmitting ? "SAVING..." : "SAVE"}
              </button>
            </div>
          </form>
        </div>

        {/* Password Form */}
        <div className="w-full lg:w-1/2 mx-auto mt-8 bg-white p-6 rounded shadow">
          <h2 className="text-xl font-bold mb-4">Change your password</h2>

          {/* Success message */}
          {passwordSuccess && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {passwordSuccess}
            </div>
          )}

          {/* Error message */}
          {passwordError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {passwordError}
            </div>
          )}

          <form>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password *
              </label>
              <input
                type="password"
                name="oldPassword"
                value={passwordData.oldPassword}
                onChange={handlePasswordChange}
                className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 ${
                  passwordValidationErrors.oldPassword
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                required
              />
              {passwordValidationErrors.oldPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {passwordValidationErrors.oldPassword}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password *
              </label>
              <input
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 ${
                  passwordValidationErrors.newPassword
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                required
              />
              {passwordValidationErrors.newPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {passwordValidationErrors.newPassword}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Must be at least 8 characters with uppercase, lowercase, and
                number
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password *
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 ${
                  passwordValidationErrors.confirmPassword
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                required
              />
              {passwordValidationErrors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {passwordValidationErrors.confirmPassword}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={handlePasswordSave}
              disabled={isPasswordSubmitting}
              className={`px-4 py-2 rounded text-white ${
                isPasswordSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600"
              }`}
            >
              {isPasswordSubmitting ? "SAVING..." : "SAVE"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditProfileStudent;
